/**
 * Specialized UI Panels for the Parametric 3D Platform
 * - ConsolidatedRightPanel: unified view controls + properties
 * - CollapsibleSidebar: organized parameter/health/export sections
 * - StatusLogViewer: tabbed log with status indicators
 */

import {
  Panel,
  CollapsibleSection,
  TabbedPanel,
  ButtonGroup,
  Flex,
  KeyValueDisplay,
  LogViewer,
  StatusIndicator,
} from './ui-components';
import { THEME, ViewMode, ViewPreset } from './ui-framework';
import { UI_ICONS } from './icons';
import type { PrintEstimate, PrintEstimateOptions } from './print-estimator';
import type { ModuleSchema } from './registry';

const PANEL_ICONS = UI_ICONS;

// ============================================================================
// CONSOLIDATED RIGHT PANEL
// ============================================================================

export interface RightPanelControls {
  viewMode: ViewMode;
  gridVisible: boolean;
  groundStyle: 'grid' | 'snapmaker' | 'solid' | 'none';
  autoRotateOn: boolean;
}

export class ConsolidatedRightPanel {
  el: HTMLElement;
  private callbacks: {
    onViewModeChange?: (mode: ViewMode) => void;
    onGridToggle?: (visible: boolean) => void;
    onGroundStyleChange?: (style: 'grid' | 'snapmaker' | 'solid' | 'none') => void;
    onAutoRotateToggle?: (on: boolean) => void;
    onZoomAll?: () => void;
    onViewPreset?: (preset: ViewPreset) => void;
    onExplodeToggle?: (exploded: boolean) => void;
    onOverhangToggle?: (on: boolean) => void;
    onMeasureToggle?: (active: boolean) => void;
    onAxisLockChange?: (axis: 'free' | 'x' | 'y' | 'z') => void;
    onSectionCutToggle?: (active: boolean, axis: 'x' | 'y' | 'z') => void;
    onProfileInspectorOpen?: () => void;
    onProfileDrawingOpen?: () => void;
    onSvgTo3DOpen?: () => void;
    onSectionOffsetChange?: (offset: number) => void;
    onLightBulbToggle?: (on: boolean) => void;
    onLightLevelChange?: (level: 'low' | 'med' | 'high') => void;
    onLightModeChange?: (mode: 'warm' | 'white' | 'mixed') => void;
    onEnvLevelChange?: (level: 'low' | 'med' | 'high') => void;
    onExportGroup?: (group: 'all' | 'shade' | 'base') => void;
    onPrintEstimateOptionsChange?: (options: PrintEstimateOptions) => void;
  } = {};

  public onLightBulbToggle(cb: (on: boolean) => void): void {
    this.callbacks.onLightBulbToggle = cb;
  }

  public onSvgTo3DOpen(cb: () => void): void { this.callbacks.onSvgTo3DOpen = cb; }

  public onLightLevelChange(cb: (level: 'low' | 'med' | 'high') => void): void {
    this.callbacks.onLightLevelChange = cb;
  }

  public onLightModeChange(cb: (mode: 'warm' | 'white' | 'mixed') => void): void {
    this.callbacks.onLightModeChange = cb;
  }

  public onEnvLevelChange(cb: (level: 'low' | 'med' | 'high') => void): void {
    this.callbacks.onEnvLevelChange = cb;
  }

  /** Reflect the cut position while the user drags the handle in the 3D view. The panel no
   *  longer drives the position, so this is the only way its readout stays truthful. */
  public setSectionPercent(percent: number): void {
    const el = document.getElementById('sectionOffsetVal');
    if (el) el.textContent = `${Math.round(percent)}%`;
  }

  public setMeasureMode(active: boolean): void {
    this.measureModeSetter?.(active);
  }

  public setSectionCutMode(active: boolean): void {
    this.sectionModeSetter?.(active);
  }

  private viewModeButtons: Map<string, HTMLButtonElement> = new Map();
  private gridToggleBtn: HTMLButtonElement | null = null;
  private autoRotateBtn: HTMLButtonElement | null = null;
  private overhangBtn: HTMLButtonElement | null = null;
  private measureBtn: HTMLButtonElement | null = null;
  private measureModeSetter: ((active: boolean) => void) | null = null;
  private sectionModeSetter: ((active: boolean) => void) | null = null;
  private printEstimateValues: HTMLElement | null = null;
  private printEstimateOptions: PrintEstimateOptions = { layerHeight: 0.2, infill: 0.15, preset: 'orca', printer: 'snapmaker', wasteMode: 'orca-layer' };
  private state: RightPanelControls = {
    viewMode: 'shape',
    gridVisible: true,
    groundStyle: 'grid',
    autoRotateOn: false,
  };

  constructor() {
    this.el = document.createElement('div');
    this.el.style.cssText = `
      position: absolute;
      top: 12px;
      right: 12px;
      background: rgba(255, 255, 255, 0.92);
      border: 1px solid #e5e5e5;
      border-radius: 8px;
      padding: 0;
      z-index: 10;
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
      min-width: 160px;
      font-family: var(--mono-font);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    `;

    this.buildLayout();
  }

  private buildLayout(): void {
    // FOUR separate cards, not one panel with rules drawn inside it. Each cluster of controls
    // gets its own card so the groups are separated by a real gap, while inside a card the
    // controls sit close together (gap 4px) rather than being spread apart.
    // Clusters: (view modes + presets + grid) / (explode) / (measure + section) / (overhang + light).
    this.el.style.cssText = `
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 8px;
      pointer-events: auto;
      font-family: var(--mono-font);
      overflow-y: auto;
      overflow-x: hidden;
      flex: 1 1 auto;
      min-height: 0;
      padding-right: 4px;
      scrollbar-width: thin;
      scrollbar-color: rgba(148, 163, 184, 0.4) transparent;
    `;

    /** Builds one card and returns the element its contents should be appended to. */
    const makeCard = (title?: string, initiallyCollapsed = false): HTMLElement => {
      const card = document.createElement('div');
      card.style.cssText = `
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid #e5e5e5;
        border-radius: 8px;
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        overflow: hidden;
        pointer-events: auto;
        flex-shrink: 0;
      `;
      const body = document.createElement('div');
      body.style.cssText = 'padding: 8px; display: flex; flex-direction: column; gap: 4px;';
      if (title) {
        const header = document.createElement('div');
        header.style.cssText = `
          padding: 8px 10px;
          background: #f5f5f2;
          border-bottom: 1px solid #e8e8e0;
          font-size: 10px;
          font-weight: 600;
          color: ${THEME.text};
          text-transform: uppercase;
          letter-spacing: 0.05em;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          user-select: none;
        `;
        const titleSpan = document.createElement('span');
        titleSpan.textContent = title;
        const arrowSpan = document.createElement('span');
        arrowSpan.innerHTML = '▾';
        arrowSpan.style.cssText = 'font-size: 9px; opacity: 0.6; transition: transform 0.2s;';
        header.appendChild(titleSpan);
        header.appendChild(arrowSpan);

        let collapsed = initiallyCollapsed;
        if (collapsed) {
          body.style.display = 'none';
          arrowSpan.innerHTML = 'â–¸';
          header.style.borderBottom = 'none';
        }
        header.addEventListener('click', () => {
          collapsed = !collapsed;
          body.style.display = collapsed ? 'none' : 'flex';
          arrowSpan.innerHTML = collapsed ? '▸' : '▾';
          header.style.borderBottom = collapsed ? 'none' : '1px solid #e8e8e0';
        });
        card.appendChild(header);
      }
      card.appendChild(body);
      this.el.appendChild(card);
      return body;
    };

    // --- PRINT ESTIMATE: driven by the currently selected part(s), or the full assembly ---
    const estimate = makeCard('🧵 3D Print Estimate', true);
    this.printEstimateValues = document.createElement('div');
    this.printEstimateValues.style.cssText = 'display:flex;flex-direction:column;gap:5px;';
    this.printEstimateValues.innerHTML = `
      <div style="font-size:9px;color:${THEME.textMuted};">Toàn bộ mô hình · nozzle 0,4 mm · layer 0,2 mm</div>
      <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:4px;">
        <div style="padding:6px;background:#f8fafc;border:1px solid ${THEME.border};border-radius:5px;">
          <div style="font-size:8px;color:${THEME.textMuted};">VẬT LIỆU IN</div><strong data-estimate="print" style="font-size:13px;">— g</strong><div data-estimate="print-length" style="font-size:9px;color:${THEME.textMuted};">— m</div>
        </div>
        <div style="padding:6px;background:#f8fafc;border:1px solid ${THEME.border};border-radius:5px;">
          <div style="font-size:8px;color:${THEME.textMuted};">VẬT LIỆU THẢI</div><strong data-estimate="waste" style="font-size:13px;">— g</strong><div data-estimate="waste-length" style="font-size:9px;color:${THEME.textMuted};">— m</div>
        </div>
        <div style="padding:6px;background:#f8fafc;border:1px solid ${THEME.border};border-radius:5px;">
          <div style="font-size:8px;color:${THEME.textMuted};">TỔNG PLA</div><strong data-estimate="material" style="font-size:13px;">— g</strong><div data-estimate="material-length" style="font-size:9px;color:${THEME.textMuted};">— m</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr;gap:5px;">
        <div style="padding:6px;background:#f8fafc;border:1px solid ${THEME.border};border-radius:5px;">
          <div style="font-size:9px;color:${THEME.textMuted};">THỜI GIAN</div><strong data-estimate="time" style="font-size:14px;">—</strong>
        </div>
      </div>
      <div style="font-size:9px;color:${THEME.textMuted};">Thể tích nhựa: <span data-estimate="volume">—</span> cm³ · <span data-estimate="layers">—</span> lớp</div>
      <div data-estimate="details" style="font-size:8px;line-height:1.35;color:${THEME.textMuted};">Ước tính: 2 thành vách, infill 15%, PLA 1,24 g/cm³.</div>`;
    estimate.appendChild(this.printEstimateValues);

    const estimateOptions = document.createElement('div');
    estimateOptions.style.cssText = 'display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:4px;margin-top:2px;';
    estimateOptions.innerHTML = `
      <label style="font-size:8px;color:${THEME.textMuted};">LAYER<select data-estimate-option="layerHeight" style="width:100%;font-size:9px;"><option value="0.12">0,12</option><option value="0.16">0,16</option><option value="0.2" selected>0,20</option></select></label>
      <label style="font-size:8px;color:${THEME.textMuted};">INFILL<select data-estimate-option="infill" style="width:100%;font-size:9px;"><option value="0.05">5%</option><option value="0.1">10%</option><option value="0.15" selected>15%</option></select></label>
      <label style="font-size:8px;color:${THEME.textMuted};">SLICER<select data-estimate-option="preset" style="width:100%;font-size:9px;"><option value="bambu">Bambu</option><option value="prusa">Prusa</option><option value="orca" selected>Orca</option></select></label>
      <label style="font-size:8px;color:${THEME.textMuted};">MÁY<select data-estimate-option="printer" style="width:100%;font-size:9px;"><option value="bambu">Bambu</option><option value="prusa">Prusa</option><option value="snapmaker" selected>Snapmaker</option></select></label>`;
    estimateOptions.innerHTML += '<label style="font-size:8px;color:' + THEME.textMuted + ';">WASTE<select data-estimate-option="wasteMode" style="width:100%;font-size:9px;"><option value="orca-layer" selected>Orca/lớp</option><option value="simple">Simple</option></select></label>';
    estimateOptions.querySelectorAll('select').forEach((select) => {
      select.addEventListener('change', () => {
        const layer = estimateOptions.querySelector('[data-estimate-option="layerHeight"]') as HTMLSelectElement;
        const infill = estimateOptions.querySelector('[data-estimate-option="infill"]') as HTMLSelectElement;
        const preset = estimateOptions.querySelector('[data-estimate-option="preset"]') as HTMLSelectElement;
        const printer = estimateOptions.querySelector('[data-estimate-option="printer"]') as HTMLSelectElement;
        const wasteMode = estimateOptions.querySelector('[data-estimate-option="wasteMode"]') as HTMLSelectElement;
        this.printEstimateOptions = {
          layerHeight: Number(layer.value) as 0.12 | 0.16 | 0.2,
          infill: Number(infill.value) as 0.05 | 0.1 | 0.15,
          preset: preset.value as PrintEstimateOptions['preset'],
          printer: printer.value as PrintEstimateOptions['printer'],
          wasteMode: wasteMode.value as PrintEstimateOptions['wasteMode'],
        };
        this.callbacks.onPrintEstimateOptionsChange?.(this.printEstimateOptions);
      });
    });
    estimate.appendChild(estimateOptions);

    // --- CARD 1a: display (shading modes + grid) ---
    let content = makeCard('🎯 Display', true);

    // --- VIEW MODE BUTTONS ---
    const viewModeGroup = this.buildViewModeGroup();
    content.appendChild(viewModeGroup);

    const groundStyleRow = document.createElement('label');
    groundStyleRow.style.cssText = `
      display:flex;align-items:center;justify-content:space-between;gap:8px;
      margin-top:8px;color:${THEME.textMuted};font-size:10px;font-family:var(--mono-font);
    `;
    groundStyleRow.innerHTML = '<span>Viewport background</span>';
    const groundStyleSelect = document.createElement('select');
    groundStyleSelect.title = 'Choose the viewport background';
    groundStyleSelect.style.cssText = `
      flex:0 0 86px;height:25px;padding:2px 4px;border:1px solid ${THEME.border};
      border-radius:4px;background:${THEME.sidebar};color:${THEME.text};font-size:10px;
    `;
    groundStyleSelect.innerHTML = '<option value="grid">Grid</option><option value="snapmaker">Snapmaker</option><option value="solid">Solid</option><option value="none">Hidden</option>';
    groundStyleSelect.value = this.state.groundStyle;
    groundStyleSelect.addEventListener('change', () => {
      const style = groundStyleSelect.value as RightPanelControls['groundStyle'];
      this.state.groundStyle = style;
      this.state.gridVisible = style === 'grid';
      this.updateGridToggleStyle();
      this.callbacks.onGroundStyleChange?.(style);
    });
    groundStyleRow.appendChild(groundStyleSelect);
    content.appendChild(groundStyleRow);

    // --- CARD 1b: camera (view presets) ---
    content = makeCard('📷 Camera');
    const presetsGroup = this.buildViewPresetsGroup();
    content.appendChild(presetsGroup);

    // --- CARD 2: tools (Consolidated 1 panel: 2 rows, 2 buttons each) ---
    content = makeCard('🛠️ Tools');

    const createToolBtn = (html: string, title: string): HTMLButtonElement => {
      const btn = document.createElement('button');
      btn.innerHTML = html;
      btn.title = title;
      btn.style.cssText = `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        flex: 1;
        min-width: 0;
        height: 28px;
        margin: 0;
        padding: 4px 6px;
        box-sizing: border-box;
        border: 1px solid ${THEME.border};
        border-radius: 4px;
        background: ${THEME.sidebar};
        color: ${THEME.text};
        font-size: 10px;
        font-family: var(--mono-font);
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      `;
      return btn;
    };

    // Row 1: Grid & Explode
    const toolsRow1 = document.createElement('div');
    toolsRow1.style.cssText = 'display: flex; gap: 6px; width: 100%;';

    this.gridToggleBtn = createToolBtn(`${PANEL_ICONS.grid}<span>Grid</span>`, 'Toggle Ground Grid');
    this.gridToggleBtn.addEventListener('click', () => {
      this.state.gridVisible = !this.state.gridVisible;
      this.updateGridToggleStyle();
      this.callbacks.onGridToggle?.(this.state.gridVisible);
    });
    this.updateGridToggleStyle();
    toolsRow1.appendChild(this.gridToggleBtn);

    const explodeBtn = createToolBtn('<span>🧩 Explode</span>', 'Explode / Attach Base Assembly');
    let isExploded = false;
    explodeBtn.addEventListener('click', () => {
      isExploded = !isExploded;
      explodeBtn.innerHTML = isExploded ? '<span>🧩 Attach</span>' : '<span>🧩 Explode</span>';
      explodeBtn.style.background = isExploded ? 'var(--accent)' : THEME.sidebar;
      explodeBtn.style.color = isExploded ? 'var(--accent-text)' : THEME.text;
      explodeBtn.style.borderColor = isExploded ? 'var(--accent)' : THEME.border;
      this.callbacks.onExplodeToggle?.(isExploded);
    });
    toolsRow1.appendChild(explodeBtn);
    content.appendChild(toolsRow1);

    // Row 2: Light & Overhang
    const toolsRow2 = document.createElement('div');
    toolsRow2.style.cssText = 'display: flex; gap: 6px; width: 100%;';

    const lightBtn = createToolBtn('<span>💡 Light</span>', 'Internal Light Simulation');
    let isLightOn = false;
    lightBtn.addEventListener('click', () => {
      isLightOn = !isLightOn;
      lightBtn.style.background = isLightOn ? '#f59e0b' : THEME.sidebar;
      lightBtn.style.color = isLightOn ? '#ffffff' : THEME.text;
      lightBtn.style.borderColor = isLightOn ? '#d97706' : THEME.border;
      lightControls.style.display = isLightOn ? 'flex' : 'none';
      this.callbacks.onLightBulbToggle?.(isLightOn);
    });
    toolsRow2.appendChild(lightBtn);

    this.overhangBtn = createToolBtn('<span>⚠️ Overhang</span>', 'Highlight Overhang >45°');
    let isOverhangOn = false;
    this.overhangBtn.addEventListener('click', () => {
      isOverhangOn = !isOverhangOn;
      this.setOverhangActive(isOverhangOn);
      this.callbacks.onOverhangToggle?.(isOverhangOn);
    });
    toolsRow2.appendChild(this.overhangBtn);
    content.appendChild(toolsRow2);

    // Light sub-controls (revealed only while the light is on)
    const lightControls = document.createElement('div');
    lightControls.style.cssText = 'display: none; flex-direction: column; gap: 3px; margin-top: 4px;';

    const buildToggleRow = <T extends string>(
      label: string,
      options: Array<{ id: T; label: string; title: string }>,
      initial: T,
      onPick: (id: T) => void,
    ): HTMLElement => {
      const row = document.createElement('div');
      row.style.cssText = 'display: flex; gap: 3px; align-items: center;';

      const lbl = document.createElement('span');
      lbl.textContent = label;
      lbl.style.cssText = `font-size: 8.5px; color: ${THEME.textMuted}; font-weight: 600; min-width: 34px;`;
      row.appendChild(lbl);

      const btns = new Map<T, HTMLButtonElement>();
      let current = initial;
      const paint = (): void => {
        btns.forEach((b, id) => {
          const on = id === current;
          b.style.background = on ? 'var(--accent)' : THEME.sidebar;
          b.style.color = on ? 'var(--accent-text)' : THEME.text;
          b.style.borderColor = on ? 'var(--accent)' : THEME.border;
        });
      };

      for (const opt of options) {
        const b = document.createElement('button');
        b.textContent = opt.label;
        b.title = opt.title;
        b.style.cssText = `
          flex: 1;
          padding: 4px 2px;
          border: 1px solid ${THEME.border};
          border-radius: 3px;
          background: ${THEME.sidebar};
          color: ${THEME.text};
          font-size: 8.5px;
          font-family: var(--mono-font);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
        `;
        b.addEventListener('click', () => {
          current = opt.id;
          paint();
          onPick(opt.id);
        });
        btns.set(opt.id, b);
        row.appendChild(b);
      }
      paint();
      return row;
    };

    lightControls.appendChild(buildToggleRow<'low' | 'med' | 'high'>(
      'Level',
      [
        { id: 'low', label: 'Low', title: 'Dim ambient glow' },
        { id: 'med', label: 'Med', title: 'Balanced room lighting' },
        { id: 'high', label: 'High', title: 'Bright - shows the hole pattern strongest' },
      ],
      'med',
      (id) => this.callbacks.onLightLevelChange?.(id),
    ));

    lightControls.appendChild(buildToggleRow<'warm' | 'white' | 'mixed'>(
      'Colour',
      [
        { id: 'warm', label: 'Warm', title: 'Warm bulb (amber)' },
        { id: 'white', label: 'White', title: 'Daylight white' },
        { id: 'mixed', label: 'Mix', title: 'Warm + white blend' },
      ],
      'warm',
      (id) => this.callbacks.onLightModeChange?.(id),
    ));

    lightControls.appendChild(buildToggleRow<'low' | 'med' | 'high'>(
      'Studio',
      [
        { id: 'low', label: 'Low', title: 'Dim room - the lamp pattern stands out most' },
        { id: 'med', label: 'Med', title: 'Balanced ambient fill' },
        { id: 'high', label: 'High', title: 'Bright fill - flatter, softer shadows' },
      ],
      'med',
      (id) => this.callbacks.onEnvLevelChange?.(id),
    ));

    content.appendChild(lightControls);

    // --- CARD 3: measure + section ---
    content = makeCard('📏 Inspect');

    // --- ROW 1: MEASURE + AXIS LOCK (Free, X, Y, Z) ---
    const measureRow = document.createElement('div');
    measureRow.style.cssText = 'display: flex; gap: 4px; align-items: center; width: 100%;';

    this.measureBtn = document.createElement('button');
    this.measureBtn.textContent = '📏 Measure';
    this.measureBtn.title = 'Toggle Point-to-Point 3D measurement';
    this.measureBtn.style.cssText = `
      flex: 1;
      min-width: 0;
      padding: 5px 6px;
      border: 1px solid ${THEME.border};
      border-radius: 4px;
      background: ${THEME.sidebar};
      color: ${THEME.text};
      font-size: 10px;
      font-family: var(--mono-font);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    `;
    let isMeasureOn = false;
    this.measureModeSetter = (active) => {
      isMeasureOn = active;
      this.setMeasureActive(active);
      this.callbacks.onMeasureToggle?.(active);
    };
    this.measureBtn.addEventListener('click', () => {
      this.measureModeSetter?.(!isMeasureOn);
    });
    measureRow.appendChild(this.measureBtn);

    const axisBtnGroup = document.createElement('div');
    axisBtnGroup.style.cssText = 'display: flex; gap: 2px; flex-shrink: 0;';

    const axes: Array<{ id: 'free' | 'x' | 'y' | 'z'; label: string; color: string }> = [
      { id: 'free', label: 'Free', color: '#64748b' },
      { id: 'x', label: 'X', color: '#ef4444' },
      { id: 'y', label: 'Y', color: '#10b981' },
      { id: 'z', label: 'Z', color: '#3b82f6' },
    ];

    let currentAxis: 'free' | 'x' | 'y' | 'z' = 'free';
    const axisBtns: Map<string, HTMLButtonElement> = new Map();

    axes.forEach((ax) => {
      const btn = document.createElement('button');
      btn.textContent = ax.label;
      btn.title = ax.id === 'free' ? '3D free distance measurement' : `Lock measurement to ${ax.label} axis`;
      btn.style.cssText = `
        padding: 4px 5px;
        border: 1px solid ${ax.id === 'free' ? THEME.accent : THEME.border};
        border-radius: 3px;
        background: ${ax.id === 'free' ? THEME.accent : THEME.sidebar};
        color: ${ax.id === 'free' ? THEME.accentText : THEME.text};
        font-size: 9px;
        font-family: var(--mono-font);
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s;
        line-height: 1;
      `;

      btn.addEventListener('click', () => {
        currentAxis = ax.id;
        axisBtns.forEach((b, k) => {
          if (k === currentAxis) {
            b.style.background = ax.id === 'free' ? THEME.accent : ax.color;
            b.style.color = '#ffffff';
            b.style.borderColor = ax.id === 'free' ? THEME.accent : ax.color;
          } else {
            b.style.background = THEME.sidebar;
            b.style.color = THEME.text;
            b.style.borderColor = THEME.border;
          }
        });
        if (!isMeasureOn) {
          this.measureModeSetter?.(true);
        }
        this.callbacks.onAxisLockChange?.(currentAxis);
      });

      axisBtns.set(ax.id, btn);
      axisBtnGroup.appendChild(btn);
    });
    measureRow.appendChild(axisBtnGroup);
    content.appendChild(measureRow);

    // --- ROW 2: SECTION VIEW + CUT AXIS (X, Y, Z) ---
    const sectionRow = document.createElement('div');
    sectionRow.style.cssText = 'display: flex; gap: 4px; align-items: center; width: 100%; margin-top: 4px;';

    const sectionToggleBtn = document.createElement('button');
    sectionToggleBtn.textContent = '✂️ Section';
    sectionToggleBtn.title = 'Toggle 3D Section View / Cut plane';
    sectionToggleBtn.style.cssText = `
      flex: 1;
      min-width: 0;
      padding: 5px 6px;
      border: 1px solid ${THEME.border};
      border-radius: 4px;
      background: ${THEME.sidebar};
      color: ${THEME.text};
      font-size: 10px;
      font-family: var(--mono-font);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    `;

    let isSectionOn = false;
    let sectionAxis: 'x' | 'y' | 'z' = 'x';

    const sectionAxisGroup = document.createElement('div');
    sectionAxisGroup.style.cssText = 'display: flex; gap: 2px; flex-shrink: 0;';

    const sAxes: Array<{ id: 'x' | 'y' | 'z'; label: string; title: string; color: string }> = [
      { id: 'x', label: 'X', title: 'Cut along X axis', color: '#ef4444' },
      { id: 'y', label: 'Y', title: 'Cut along Y axis', color: '#10b981' },
      { id: 'z', label: 'Z', title: 'Cut along Z axis', color: '#3b82f6' },
    ];
    const sBtns: Map<string, HTMLButtonElement> = new Map();

    const updateSectionBtnStyle = () => {
      sectionToggleBtn.style.background = isSectionOn ? 'var(--accent)' : THEME.sidebar;
      sectionToggleBtn.style.color = isSectionOn ? 'var(--accent-text)' : THEME.text;
      sectionToggleBtn.style.borderColor = isSectionOn ? 'var(--accent)' : THEME.border;
    };

    const updateSectionAxisBtns = () => {
      sBtns.forEach((b, k) => {
        const axObj = sAxes.find((a) => a.id === k);
        if (k === sectionAxis) {
          b.style.background = axObj?.color || THEME.accent;
          b.style.color = '#ffffff';
          b.style.borderColor = axObj?.color || THEME.accent;
        } else {
          b.style.background = THEME.sidebar;
          b.style.color = THEME.text;
          b.style.borderColor = THEME.border;
        }
      });
    };

    sAxes.forEach((ax) => {
      const btn = document.createElement('button');
      btn.textContent = ax.label;
      btn.title = ax.title;
      btn.style.cssText = `
        padding: 4px 7px;
        min-width: 20px;
        border: 1px solid ${ax.id === 'x' ? ax.color : THEME.border};
        border-radius: 3px;
        background: ${ax.id === 'x' ? ax.color : THEME.sidebar};
        color: ${ax.id === 'x' ? '#ffffff' : THEME.text};
        font-size: 9px;
        font-family: var(--mono-font);
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s;
        line-height: 1;
      `;
      btn.addEventListener('click', () => {
        sectionAxis = ax.id;
        updateSectionAxisBtns();
        this.sectionModeSetter?.(true);
      });
      sBtns.set(ax.id, btn);
      sectionAxisGroup.appendChild(btn);
    });

    const offsetReadout = document.createElement('div');
    offsetReadout.style.cssText = 'display: none; font-size: 8.5px; color: ' + THEME.textMuted + '; text-align: center; margin-top: 2px;';
    offsetReadout.innerHTML = 'Cut <span id="sectionOffsetVal">50%</span> — drag the handle in the view';

    this.sectionModeSetter = (active) => {
      isSectionOn = active;
      updateSectionBtnStyle();
      offsetReadout.style.display = active ? 'block' : 'none';
      this.callbacks.onSectionCutToggle?.(active, sectionAxis);
    };

    sectionToggleBtn.addEventListener('click', () => {
      this.sectionModeSetter?.(!isSectionOn);
    });

    const profileBtn = document.createElement('button');
    profileBtn.textContent = 'Profile';
    profileBtn.title = 'Open editable 2D profile from the active section cut';
    profileBtn.style.cssText = `
      flex: 0 0 auto;
      padding: 5px 7px;
      border: 1px solid ${THEME.border};
      border-radius: 4px;
      background: ${THEME.sidebar};
      color: ${THEME.text};
      font-size: 10px;
      font-family: var(--mono-font);
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
    `;
    profileBtn.addEventListener('click', () => this.callbacks.onProfileInspectorOpen?.());

    sectionRow.appendChild(sectionToggleBtn);
    sectionRow.appendChild(sectionAxisGroup);
    content.appendChild(sectionRow);
    content.appendChild(offsetReadout);

    const profileRow = document.createElement('div');
    profileRow.style.cssText = 'display: flex; width: 100%; margin-top: 4px;';
    profileBtn.style.cssText += 'width: 100%; flex: 1 1 auto;';
    profileRow.appendChild(profileBtn);
    content.appendChild(profileRow);

    const drawingBtn = document.createElement('button');
    drawingBtn.textContent = 'Draw Profile';
    drawingBtn.title = 'Open the independent profile drawing tool';
    drawingBtn.style.cssText = `width:100%;margin-top:5px;padding:6px 7px;border:1px solid ${THEME.border};border-radius:4px;background:#eff6ff;color:#1d4ed8;font-size:10px;font-family:var(--mono-font);font-weight:600;cursor:pointer;`;
    drawingBtn.addEventListener('click', () => this.callbacks.onProfileDrawingOpen?.());
    content.appendChild(drawingBtn);

    const svg3dBtn = document.createElement('button');
    svg3dBtn.textContent = 'SVG to 3D';
    svg3dBtn.title = 'Load an SVG and extrude it with the standard SVGLoader pipeline';
    svg3dBtn.style.cssText = `width:100%;margin-top:5px;padding:6px 7px;border:1px solid ${THEME.border};border-radius:4px;background:#ecfdf5;color:#047857;font-size:10px;font-family:var(--mono-font);font-weight:600;cursor:pointer;`;
    svg3dBtn.addEventListener('click', () => this.callbacks.onSvgTo3DOpen?.());
    content.appendChild(svg3dBtn);

    // Keep the frequently used view/tool controls first and the estimate card at the bottom.
    if (estimate.parentElement) this.el.appendChild(estimate.parentElement);
  }

  private exportContainer: HTMLElement | null = null;
  private exportButtons: Map<string, HTMLButtonElement> = new Map();

  public setSelectedGroup(group: string): void {
    this.exportButtons.forEach((btn, id) => {
      if (id === group) {
        btn.style.borderColor = 'var(--accent)';
        btn.style.boxShadow = '0 0 0 1.5px var(--accent)';
      } else {
        btn.style.borderColor = THEME.border;
        btn.style.boxShadow = 'none';
      }
    });
  }

  public setPrintEstimate(value: PrintEstimate | null): void {
    if (!this.printEstimateValues) return;
    if (!value) {
      this.printEstimateValues.querySelector('[data-estimate="material"]')!.textContent = '— g';
      this.printEstimateValues.querySelector('[data-estimate="print"]')!.textContent = '— g';
      this.printEstimateValues.querySelector('[data-estimate="waste"]')!.textContent = '— g';
      this.printEstimateValues.querySelector('[data-estimate="print-length"]')!.textContent = '— m';
      this.printEstimateValues.querySelector('[data-estimate="waste-length"]')!.textContent = '— m';
      this.printEstimateValues.querySelector('[data-estimate="material-length"]')!.textContent = '— m';
      this.printEstimateValues.querySelector('[data-estimate="time"]')!.textContent = '—';
      this.printEstimateValues.querySelector('[data-estimate="volume"]')!.textContent = '—';
      this.printEstimateValues.querySelector('[data-estimate="layers"]')!.textContent = '—';
      return;
    }
    const totalMinutes = Math.max(1 / 60, value.timeMinutes);
    const time = totalMinutes < 10
      ? `~${totalMinutes.toFixed(1).replace('.', ',')} phút`
      : (() => {
          const roundedMinutes = Math.max(1, Math.round(totalMinutes));
          const hours = Math.floor(roundedMinutes / 60);
          const minutes = roundedMinutes % 60;
          return hours > 0 ? `${hours} giờ ${minutes} phút` : `${minutes} phút`;
        })();
    const scopeLine = this.printEstimateValues.querySelector('div');
    if (scopeLine) scopeLine.textContent = `${value.scope} · ${value.preset} + ${value.printer} · layer ${value.layerHeight.toFixed(2).replace('.', ',')} mm`;
    // Small parts (especially embossed text) commonly weigh below 0.1 g.
    // Showing only one decimal rounded 0.0496 g to "0.0 g", which falsely
    // suggested that the selected part had no material.
    const formatGrams = (grams: number): string => grams === 0
      ? '0 g'
      : grams < 0.1
        ? `${grams.toFixed(2)} g`
        : `${grams.toFixed(grams < 10 ? 1 : 0)} g`;
    const materialText = formatGrams(value.materialGrams);
    const formatMeters = (meters: number): string => `${meters < 0.1 ? meters.toFixed(2) : meters.toFixed(1).replace('.', ',')} m`;
    this.printEstimateValues.querySelector('[data-estimate="print"]')!.textContent = formatGrams(value.printGrams);
    this.printEstimateValues.querySelector('[data-estimate="waste"]')!.textContent = formatGrams(value.wasteGrams);
    this.printEstimateValues.querySelector('[data-estimate="material"]')!.textContent = materialText;
    this.printEstimateValues.querySelector('[data-estimate="print-length"]')!.textContent = formatMeters(value.printLengthM);
    this.printEstimateValues.querySelector('[data-estimate="waste-length"]')!.textContent = formatMeters(value.wasteLengthM);
    this.printEstimateValues.querySelector('[data-estimate="material-length"]')!.textContent = formatMeters(value.materialLengthM);
    this.printEstimateValues.querySelector('[data-estimate="time"]')!.textContent = time;
    this.printEstimateValues.querySelector('[data-estimate="volume"]')!.textContent = value.volumeCm3.toFixed(2);
    this.printEstimateValues.querySelector('[data-estimate="layers"]')!.textContent = String(value.layers);
    const details = this.printEstimateValues.querySelector('[data-estimate="details"]');
    if (details) {
      const purge = value.purgeGrams > 0 ? ` Purge: ${value.purgeGrams.toFixed(2).replace('.', ',')} g.` : ' Không purge (1 màu).';
      const mode = value.wasteMode === 'orca-layer' ? 'Orca từng lớp' : 'Simple';
      details.textContent = `Ước tính: 2 thành vách, infill ${Math.round(value.infill * 100)}%, ${mode}, PLA 1,24 g/cm³, filament 1,75 mm.${purge}`;
    }
  }

  public getPrintEstimateOptions(): PrintEstimateOptions {
    return { ...this.printEstimateOptions };
  }

  public setModuleSchema(schema: Pick<ModuleSchema, 'exportGroups'>): void {
    if (!this.exportContainer) return;
    this.exportContainer.innerHTML = '';
    this.exportButtons.clear();

    const exportGroups = (schema.exportGroups ?? [
      { id: 'shade',  label: 'Download STL - Lampshade (Part 0)' },
      { id: 'base',   label: 'Download STL - E27 Base (Part 1)' },
      { id: 'screws', label: 'Download STL - Screws (Part 2)' },
      { id: 'all',    label: 'Download STL - Full Assembly' },
    ]).map((group, index) => ({
      id: group.id ?? group.groupId?.toString() ?? `group_${index}`,
      label: group.label ?? group.name ?? `Export ${index + 1}`,
    }));

    exportGroups.forEach((btnInfo) => {
      const btn = document.createElement('button');
      btn.textContent = btnInfo.label;
      btn.style.cssText = `
        padding: 5px 8px;
        border: 1px solid ${THEME.border};
        border-radius: 4px;
        background: ${btnInfo.id === 'all' ? 'var(--accent)' : THEME.sidebar};
        color: ${btnInfo.id === 'all' ? 'var(--accent-text)' : THEME.text};
        font-size: 9.5px;
        font-family: var(--mono-font);
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s;
        text-align: left;
      `;
      btn.addEventListener('mouseenter', () => {
        btn.style.opacity = '0.85';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.opacity = '1.0';
      });
      btn.addEventListener('click', () => {
        this.callbacks.onExportGroup?.(btnInfo.id as any);
      });
      this.exportButtons.set(btnInfo.id, btn);
      this.exportContainer!.appendChild(btn);
    });
  }

  private buildViewModeGroup(): HTMLElement {
    const group = document.createElement('div');
    group.style.cssText = `
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
    `;

    const modes: Array<{ id: ViewMode; label: string; icon: string; title: string }> = [
      { id: 'shape', label: 'Solid', icon: PANEL_ICONS.solid, title: 'Realistic PBR solid material' },
      { id: 'wireframe', label: 'Wire', icon: PANEL_ICONS.wire, title: 'Triangle mesh wireframe' },
      { id: 'overlay', label: 'Overlay', icon: PANEL_ICONS.overlay, title: 'Solid with edge wireframe overlay' },
      { id: 'xray', label: 'X-Ray', icon: PANEL_ICONS.xray, title: 'See-through internal structure & cavity' },
      { id: 'normals', label: 'Normal', icon: PANEL_ICONS.normal, title: 'Surface normal vectors (XYZ -> RGB)' },
    ];

    modes.forEach((mode, idx) => {
      const btn = document.createElement('button');
      btn.dataset.viewMode = mode.id;
      btn.innerHTML = `${mode.icon}<span>${mode.label}</span>`;
      btn.title = mode.title;
      btn.style.cssText = `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 6px 8px;
        border: 1px solid ${THEME.border};
        border-radius: 4px;
        background: ${THEME.sidebar};
        color: ${THEME.textMuted};
        font-size: 10px;
        font-family: var(--mono-font);
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s;
        ${idx === modes.length - 1 ? 'grid-column: 1 / -1;' : ''}
      `;

      btn.addEventListener('click', () => {
        this.setViewMode(mode.id);
      });

      btn.addEventListener('mouseenter', () => {
        if (this.state.viewMode !== mode.id) {
          btn.style.color = THEME.text;
          btn.style.background = '#f1f5f9';
        }
      });

      btn.addEventListener('mouseleave', () => {
        if (this.state.viewMode !== mode.id) {
          btn.style.color = THEME.textMuted;
          btn.style.background = THEME.sidebar;
        }
      });

      this.viewModeButtons.set(mode.id, btn);
      group.appendChild(btn);
    });

    this.setViewMode('shape');
    return group;
  }

  private buildViewPresetsGroup(): HTMLElement {
    const group = document.createElement('div');
    group.style.cssText = `
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 4px;
    `;

    // Row 1: two isometric corners + top/bottom. Row 2: front/back/left/right.
    const presets: Array<{ id: ViewPreset; label: string; icon: string; title: string }> = [
      { id: 'iso', label: 'Iso 1', icon: PANEL_ICONS.iso, title: 'Isometric view 1 (front-right-top)' },
      { id: 'iso2', label: 'Iso 2', icon: PANEL_ICONS.iso2, title: 'Isometric view 2 (back-left-top)' },
      { id: 'top', label: 'Top', icon: PANEL_ICONS.top, title: 'Top view (7)' },
      { id: 'bottom', label: 'Bottom', icon: PANEL_ICONS.bottom, title: 'Bottom view (9)' },
      { id: 'front', label: 'Front', icon: PANEL_ICONS.front, title: 'Front view (1)' },
      { id: 'back', label: 'Back', icon: PANEL_ICONS.back, title: 'Back view' },
      { id: 'left', label: 'Left', icon: PANEL_ICONS.left, title: 'Left view' },
      { id: 'right', label: 'Right', icon: PANEL_ICONS.right, title: 'Right view (3)' },
    ];

    presets.forEach((preset) => {
      const btn = document.createElement('button');
      btn.innerHTML = `${preset.icon}<span>${preset.label}</span>`;
      btn.title = preset.title;
      btn.style.cssText = `
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        margin: 0;
        padding: 4px 2px;
        min-width: 0;
        box-sizing: border-box;
        border: 1px solid ${THEME.border};
        border-radius: 4px;
        background: ${THEME.sidebar};
        color: ${THEME.text};
        font-size: 8.5px;
        font-family: var(--mono-font);
        font-weight: 500;
        cursor: pointer;
        transition: all 0.15s;
      `;

      btn.addEventListener('click', () => {
        this.callbacks.onViewPreset?.(preset.id);
      });

      btn.addEventListener('mouseenter', () => {
        btn.style.background = '#f0f0ed';
        btn.style.borderColor = THEME.text;
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.background = THEME.sidebar;
        btn.style.borderColor = THEME.border;
      });

      group.appendChild(btn);
    });

    return group;
  }

  private setViewMode(mode: ViewMode): void {
    this.state.viewMode = mode;

    // Update button styles
    this.viewModeButtons.forEach((btn, id) => {
      if (id === mode) {
        btn.style.background = THEME.accent;
        btn.style.color = THEME.accentText;
        btn.style.borderColor = THEME.accent;
      } else {
        btn.style.background = THEME.sidebar;
        btn.style.color = THEME.textMuted;
        btn.style.borderColor = THEME.border;
      }
    });

    this.callbacks.onViewModeChange?.(mode);
  }

  private updateGridToggleStyle(): void {
    if (!this.gridToggleBtn) return;
    if (this.state.gridVisible) {
      this.gridToggleBtn.style.background = THEME.accent;
      this.gridToggleBtn.style.color = THEME.accentText;
      this.gridToggleBtn.style.borderColor = THEME.accent;
    } else {
      this.gridToggleBtn.style.background = THEME.sidebar;
      this.gridToggleBtn.style.color = THEME.text;
      this.gridToggleBtn.style.borderColor = THEME.border;
    }
  }

  public setOverhangActive(active: boolean): void {
    if (!this.overhangBtn) return;
    this.overhangBtn.style.background = active ? 'var(--accent)' : THEME.sidebar;
    this.overhangBtn.style.color = active ? 'var(--accent-text)' : THEME.text;
    this.overhangBtn.style.borderColor = active ? 'var(--accent)' : THEME.border;
  }

  public setMeasureActive(active: boolean): void {
    if (!this.measureBtn) return;
    this.measureBtn.style.background = active ? 'var(--accent)' : THEME.sidebar;
    this.measureBtn.style.color = active ? 'var(--accent-text)' : THEME.text;
    this.measureBtn.style.borderColor = active ? 'var(--accent)' : THEME.border;
  }

  private updateAutoRotateStyle(): void {
    if (!this.autoRotateBtn) return;
    if (this.state.autoRotateOn) {
      this.autoRotateBtn.style.background = THEME.accent;
      this.autoRotateBtn.style.color = THEME.accentText;
      this.autoRotateBtn.style.borderColor = THEME.accent;
    } else {
      this.autoRotateBtn.style.background = THEME.sidebar;
      this.autoRotateBtn.style.color = THEME.text;
      this.autoRotateBtn.style.borderColor = THEME.border;
    }
  }

  onViewModeChange(cb: (mode: ViewMode) => void): this {
    this.callbacks.onViewModeChange = cb;
    return this;
  }

  onGridToggle(cb: (visible: boolean) => void): this {
    this.callbacks.onGridToggle = cb;
    return this;
  }

  onAutoRotateToggle(cb: (on: boolean) => void): this {
    this.callbacks.onAutoRotateToggle = cb;
    return this;
  }

  onZoomAll(cb: () => void): this {
    this.callbacks.onZoomAll = cb;
    return this;
  }

  onExplodeToggle(cb: (exploded: boolean) => void): this {
    this.callbacks.onExplodeToggle = cb;
    return this;
  }

  onOverhangToggle(cb: (on: boolean) => void): this {
    this.callbacks.onOverhangToggle = cb;
    return this;
  }

  onMeasureToggle(cb: (active: boolean) => void): this {
    this.callbacks.onMeasureToggle = cb;
    return this;
  }

  onAxisLockChange(cb: (axis: 'free' | 'x' | 'y' | 'z') => void): this {
    this.callbacks.onAxisLockChange = cb;
    return this;
  }

  onSectionCutToggle(cb: (active: boolean, axis: 'x' | 'y' | 'z') => void): this {
    this.callbacks.onSectionCutToggle = cb;
    return this;
  }

  onGroundStyleChange(cb: (style: RightPanelControls['groundStyle']) => void): this {
    this.callbacks.onGroundStyleChange = cb;
    return this;
  }

  onProfileInspectorOpen(cb: () => void): this {
    this.callbacks.onProfileInspectorOpen = cb;
    return this;
  }

  onProfileDrawingOpen(cb: () => void): this {
    this.callbacks.onProfileDrawingOpen = cb;
    return this;
  }

  onSectionOffsetChange(cb: (offset: number) => void): this {
    this.callbacks.onSectionOffsetChange = cb;
    return this;
  }

  onExportGroup(cb: (group: 'all' | 'shade' | 'base') => void): this {
    this.callbacks.onExportGroup = cb;
    return this;
  }

  onViewPreset(cb: (preset: ViewPreset) => void): this {
    this.callbacks.onViewPreset = cb;
    return this;
  }

  onPrintEstimateOptionsChange(cb: (options: PrintEstimateOptions) => void): this {
    this.callbacks.onPrintEstimateOptionsChange = cb;
    return this;
  }
}

// ============================================================================
// COLLAPSIBLE SIDEBAR
// ============================================================================

export class CollapsibleSidebar {
  el: HTMLElement;
  sectionsMap: Map<string, CollapsibleSection> = new Map();

  constructor() {
    this.el = document.createElement('div');
    this.el.style.cssText = `
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow-y: auto;
    `;
  }

  addSection(id: string, title: string, expanded: boolean = true): CollapsibleSection {
    const section = new CollapsibleSection(title, expanded);
    this.sectionsMap.set(id, section);
    this.el.appendChild(section.el);
    return section;
  }

  getSection(id: string): CollapsibleSection | undefined {
    return this.sectionsMap.get(id);
  }

  addSpacer(): void {
    const spacer = document.createElement('div');
    spacer.style.cssText = 'flex: 1;';
    this.el.appendChild(spacer);
  }

  addFooter(content: HTMLElement): void {
    const footer = document.createElement('div');
    footer.style.cssText = `
      padding: 12px;
      border-top: 1px solid ${THEME.border};
      background: #fafaf7;
      font-size: 11px;
      color: ${THEME.textMuted};
    `;
    footer.appendChild(content);
    this.el.appendChild(footer);
  }
}

// ============================================================================
// STATUS LOG VIEWER (Tabbed)
// ============================================================================

export class StatusLogViewer {
  el: HTMLElement;
  private statusContent: HTMLElement;
  private dotEl: HTMLElement;
  private currentStatus: 'ok' | 'warning' | 'error' = 'ok';

  constructor() {
    this.el = document.createElement('div');
    // align-self:stretch + box-sizing make this panel exactly as wide as the widest sibling in
    // left-overlay-col. Without an explicit stretch a block with fit-content content would size
    // to its own text and sit narrower than the hierarchy panel above it.
    this.el.style.cssText = `
      align-self: stretch;
      box-sizing: border-box;
      padding: 8px 12px;
      font-family: var(--mono-font);
      font-size: 11px;
      color: #1a1a1a;
      line-height: 1.4;
      overflow-wrap: anywhere;
    `;

    // Status dot
    this.dotEl = document.createElement('span');
    this.dotEl.style.cssText = `
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #15803d;
      margin-right: 8px;
      vertical-align: middle;
    `;
    this.el.appendChild(this.dotEl);

    // Status text
    this.statusContent = document.createElement('span');
    this.statusContent.textContent = 'Ready';
    this.el.appendChild(this.statusContent);
  }

  setStatus(message: string, status: 'ok' | 'warning' | 'error' | 'info' = 'ok'): void {
    const mappedStatus = status === 'info' ? 'ok' : status;
    this.currentStatus = mappedStatus as 'ok' | 'warning' | 'error';
    
    // Update dot color
    const colors = {
      ok: '#15803d',
      warning: '#f97316',
      error: '#dc2626'
    };
    this.dotEl.style.background = colors[mappedStatus] || colors.ok;
    
    // Update text
    this.statusContent.textContent = message;
  }
}
